'use client';

import { useUser } from '@/features/auth/shared/hooks/use-user';
import { AlertTriangle } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { emitGTMEvent } from '@/features/gtm/shared/helper/emit-gtm-event';
import { useEffect } from 'react';
import { useAppStore } from '@/features/shared/app-store';
import { Link } from '@/i18n/routing';
import { accountRoutes } from '@/shared/constants/routes';

export const MembershipPaymentReminder = () => {
  const { loading, authenticatedUser } = useUser();
  const t = useTranslations('WildMemberStatus.paymentReminder');

  const memberStatus = authenticatedUser?.membershipDetails?.status;
  const shouldShowBanner = !loading && memberStatus === 'past_due';
  const umsGuid = authenticatedUser?.umsGuid;

  const hasSentThisSession = useAppStore((s) => s.gtmHasSentThisSession);
  const markSentThisSession = useAppStore((s) => s.gtmMarkSentThisSession);

  useEffect(() => {
    if (!shouldShowBanner || !umsGuid) return;

    const key = `PAYMENT_PAST_DUE_REMINDER_DISPLAYED:${umsGuid}`;
    if (hasSentThisSession(key)) return;

    emitGTMEvent('PAYMENT_PAST_DUE_REMINDER_DISPLAYED');
    markSentThisSession(key);
  }, [shouldShowBanner, umsGuid, hasSentThisSession, markSentThisSession]);

  if (!shouldShowBanner) {
    return null;
  }

  return (
    <div className='fixed inset-x-0 bottom-0 z-50'>
      <div className='border-error border-t bg-zinc-900'>
        <div className='mx-auto max-w-screen-xl px-4 py-3 sm:px-6'>
          <div className='flex items-center gap-3'>
            <AlertTriangle className='text-error h-5 w-5 shrink-0' />
            <p className='text-error text-sm sm:text-base'>
              <span className='text-error font-semibold'>{t('title')}</span> {t('description')}
            </p>
            <div className='ml-auto'>
              <Link
                href={accountRoutes.MEMBERSHIP_UPDATE}
                className='bg-error rounded-md px-3 py-1.5 text-sm font-medium text-white'
                data-gtm='cta'
                data-cta-name='payment_past_due_reminder'
              >
                {t('updatePayment')}
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
